import React, { useCallback, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Button, Stack, SvgIconProps, Typography } from "@mui/material";
import { K1FileInCircleGroup, PsychologyReverse } from "assets/material-custom-icons";
import { useDevice } from "hooks";

interface Props {
  onFileUpload: (preselectedFileList?: FileList) => void;
}

export const Empty: React.FC<Props> = ({ onFileUpload }) => {
  const { isMobile } = useDevice();
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the main container, not a child element
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const allFiles = e.dataTransfer.files;
      const pdfFiles = Array.from(allFiles).filter((file) => file.type === "application/pdf");

      if (pdfFiles.length > 0) {
        const dataTransfer = new DataTransfer();
        pdfFiles.forEach((file) => dataTransfer.items.add(file));
        onFileUpload(dataTransfer.files);
      }
    },
    [onFileUpload]
  );

  return (
    <Stack
      sx={{
        flexGrow: 1,
        gap: isMobile ? "10px" : "20px",
        p: "20px",
        borderRadius: "8px",
        border: isDragging ? "2px dashed #7b92a9" : "1px dashed #E0E0E0",
        alignItems: "center",
        bgcolor: isDragging ? "#E8F0F8" : "#F6F8FC",
        transition: "all 0.2s ease-in-out"
      }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Typography variant="body1" sx={{ color: "#0A3142", ...(isMobile && { maxWidth: 192, textAlign: "center" }) }}>
        Scan documents and get value you need at once.
      </Typography>
      <Stack
        sx={{
          ...(isMobile
            ? { flexDirection: "column", gap: "5px" }
            : { flexDirection: "row", width: "100%", justifyContent: "center", gap: "10px" })
        }}
      >
        <EmptyItem circleWithIcon={<K1FileInCircleGroup sx={{ width: 36, height: 36 }} />} text="K1 Extractor" />
        <EmptyItem Icon={DescriptionOutlinedIcon} text="Capital Account Statement" />
        <EmptyItem Icon={PsychologyReverse} text="Capital Calls Data Extractor" />
      </Stack>
      <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "10px" }}>
        {!isMobile && (
          <Typography variant="body3" sx={{ color: "#496682" }}>
            Drag and drop or
          </Typography>
        )}
        <Button
          onClick={() => onFileUpload()}
          size="sm"
          startIcon={<AddIcon sx={{ color: "#0A3142" }} />}
          variant="outlined"
        >
          Upload File
        </Button>
      </Stack>
    </Stack>
  );
};

interface EmptyItemProps {
  Icon?: React.ComponentType<SvgIconProps>;
  text: string;
  circleWithIcon?: React.JSX.Element;
}

export const EmptyItem: React.FC<EmptyItemProps> = ({ Icon, text, circleWithIcon }) => {
  return (
    <Stack
      sx={{
        flexDirection: "row",
        alignItems: "center",
        gap: "10px",
        p: "12px",
        borderRadius: "8px",
        maxWidth: "192px",
        overflow: "hidden",
        flexGrow: 1,
        bgcolor: "white"
      }}
    >
      {circleWithIcon ? (
        circleWithIcon
      ) : (
        <Stack
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: "#C09560",
            alignItems: "center",
            justifyContent: "center",
            position: "relative"
          }}
        >
          {Icon && <Icon sx={{ color: "white", fill: "white" }} />}
        </Stack>
      )}
      <Typography variant="body2" sx={{ color: "#496682", fontWeight: 500, lineHeight: "20px" }}>
        {text}
      </Typography>
    </Stack>
  );
};
